import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cleanTagName, defaultTagColor, tagNameKey, type Tag } from '@tally/shared';
import { Plus, Sparkles, Tag as TagIcon } from 'lucide-react';
import { tagsApi, type TagSuggestion } from '@/services/api';
import { cn } from '@/utils/cn';

interface TagPickerProps {
  tags: Tag[];
  /** Tags not to offer, e.g. ones the expense already has. */
  excludeIds?: string[];
  onPick: (tag: { id: string; name: string }) => void;
  /** AI ideas shown while the box is empty; one without a tagId is created when picked. */
  suggestions?: TagSuggestion[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** Type to find a tag; a name that does not exist yet can be created on the spot. */
export function TagPicker({ tags, excludeIds = [], onPick, suggestions = [], placeholder = 'Add tag…', disabled, className }: TagPickerProps) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = tagNameKey(text);
  const options = useMemo(() => {
    const excluded = new Set(excludeIds);
    return tags
      .filter((t) => !excluded.has(t.id) && (!query || tagNameKey(t.name).includes(query)))
      .sort((a, b) => Number(tagNameKey(b.name).startsWith(query)) - Number(tagNameKey(a.name).startsWith(query)) || b.transactionCount - a.transactionCount)
      .slice(0, 8);
  }, [tags, excludeIds, query]);
  const exact = tags.find((t) => tagNameKey(t.name) === query);
  const newName = cleanTagName(text);
  const canCreate = !!newName && !exact;

  const pick = (tag: { id: string; name: string }) => {
    onPick(tag);
    setText('');
    setOpen(false);
    setError(null);
  };

  const shownSuggestions = query ? [] : suggestions.filter((s) => !s.tagId || !excludeIds.includes(s.tagId));

  const create = async (name: string | null = newName) => {
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const res = await tagsApi.create({ name });
      await queryClient.invalidateQueries({ queryKey: ['tags'] });
      pick({ id: res.data.id, name });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the tag');
    } finally {
      setCreating(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (exact) { if (!excludeIds.includes(exact.id)) pick(exact); }
    else if (canCreate) void create(newName);
    else if (options[0]) pick(options[0]);
  };

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-ring">
        <TagIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={text}
          disabled={disabled || creating}
          placeholder={placeholder}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          className="w-full min-w-[120px] bg-transparent text-sm focus:outline-none disabled:opacity-50"
          aria-label={placeholder}
        />
      </div>
      {open && (options.length > 0 || canCreate || shownSuggestions.length > 0) && (
        <ul className="absolute left-0 right-0 z-20 mt-1 max-h-64 overflow-auto rounded-lg border border-border bg-card py-1 shadow-lg">
          {shownSuggestions.map((sg) => (
            <li key={`s-${sg.name}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => (sg.tagId ? pick({ id: sg.tagId, name: sg.name }) : void create(sg.name))}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="flex-1 truncate">{sg.name}</span>
                <span className="text-xs text-muted-foreground">suggested</span>
              </button>
            </li>
          ))}
          {shownSuggestions.length > 0 && options.length > 0 && <li className="my-1 border-t border-border" role="separator" />}
          {options.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(t)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: t.color ?? defaultTagColor(t.name) }} />
                <span className="flex-1 truncate">{t.name}</span>
                <span className="text-xs text-muted-foreground">{t.transactionCount}</span>
              </button>
            </li>
          ))}
          {canCreate && (
            <li>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void create(newName)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-primary hover:bg-accent"
              >
                <Plus className="h-3.5 w-3.5" /> Create “{newName}”
              </button>
            </li>
          )}
        </ul>
      )}
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
